import React, { useEffect, useState, useCallback, useMemo } from 'react';
import SimplePdfViewer from '../components/SimplePdfViewer';
import api from '../api/http';
import { Form, Input, Select, DatePicker, Button, Card, Table, Tag, Space, Modal, message, Drawer, Spin, AutoComplete, Upload } from 'antd';
import dayjs from 'dayjs';
import { useAuth } from '../context/AuthContext';
import { openWithAuth } from '../utils/authedResource';

const CHANGE_TYPES = [
  { value:'hire', label:'入职' },
  { value:'transfer', label:'调岗' },
  { value:'resign', label:'离职' },
  { value:'salary_adjust', label:'薪资调整' },
  { value:'probation_confirm', label:'转正' },
  { value:'promotion', label:'晋升' },
  { value:'demotion', label:'降级' },
  { value:'other', label:'其它' }
];

function EmployeeChangesPage() {
  const { user, hasPermission } = useAuth();
  // 状态声明
  const [activeTab, setActiveTab] = useState('create');
  const [step, setStep] = useState(1);
  const [changeType, setChangeType] = useState();
  const [form] = Form.useForm();
  const [filterForm] = Form.useForm();
  const [myList, setMyList] = useState([]);
  const [pendingList, setPendingList] = useState([]);
  // id->姓名缓存
  const [employeeNameMap, setEmployeeNameMap] = useState({});
  // 组织元数据缓存，必须在使用前初始化
  const [orgMetaCache, setOrgMetaCache] = useState({ department: [], section: [], team: [], position: [] });

  // 批量查员工姓名（兼容后端不支持多 id 查询的情况）
  const fetchEmployeeNames = async (ids) => {
    if (!ids || !ids.length) return {};
    const map = {};
    for (const id of ids) {
      try {
        const res = await api.get(`/employees?id=${id}`);
        if (Array.isArray(res.data) && res.data.length > 0) {
          map[res.data[0].id] = res.data[0].name;
        }
      } catch (e) { /* ignore */ }
    }
    return map;
  };
  const [loading, setLoading] = useState(false);
  const [decisionModal, setDecisionModal] = useState({ open: false, record: null });
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [notifyVisible, setNotifyVisible] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [filters, setFilters] = useState({});

  // --- 入职流程看板样式与状态 ---
  const flowCardContainerStyle = { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 };
  const [isNarrow, setIsNarrow] = useState(typeof window !== 'undefined' ? window.innerWidth < 720 : false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setIsNarrow(window.innerWidth < 720);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const flowStepStyle = (bg, accent, isLast) => ({
    flex: isNarrow ? '1 1 auto' : '1 1 180px',
    minWidth: isNarrow ? 'auto' : 160,
    background: bg,
    borderRadius: 12,
    padding: isNarrow ? '12px 14px' : '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    boxShadow: '0 8px 20px rgba(15,23,42,0.06)',
    border: `1px solid ${accent}33`,
    clipPath: isNarrow ? 'none' : (isLast ? 'polygon(0 0,100% 0,100% 100%,0 100%)' : 'polygon(0 0,calc(100% - 24px) 0,100% 50%,calc(100% - 24px) 100%,0 100%)')
  });
  const flowStepIndex = (accent) => ({ width: isNarrow ? 30 : 34, height: isNarrow ? 30 : 34, borderRadius: '50%', background: accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: isNarrow ? 14 : 16 });
  const flowStepTitle = { fontSize: isNarrow ? 14 : 15, fontWeight: 700, color: '#0f172a' };
  const flowStepDesc = { fontSize: isNarrow ? 12 : 13, color: '#475569', lineHeight: 1.5 };
  const flowStepMetric = (accent) => ({ marginTop: 'auto', fontSize: isNarrow ? 12 : 13, color: accent, fontWeight: 700 });

  // combined list helper (kept for future use)
  const allApplications = useMemo(() => (Array.isArray(myList) ? myList.concat(Array.isArray(pendingList) ? pendingList : []) : (Array.isArray(pendingList) ? pendingList : [])), [myList, pendingList]);
  // 资料预览弹窗
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: '', fileName: '' });
  // 选项数据
  const [deptOptions, setDeptOptions] = useState([]);
  const [sectionOptions, setSectionOptions] = useState([]);
  const [teamOptions, setTeamOptions] = useState([]);
  const [positionOptions, setPositionOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  // 上传弹窗相关：可选资料类型
  const [docTypeOptions, setDocTypeOptions] = useState([
    { value: 'uniform', label: '回收工作服' },
    { value: 'resignation', label: '辞职单（会签完成）' },
    { value: 'medical', label: '离职体检报告书' }
  ]);
  // 权限、映射等
  const userRoles = useMemo(() => {
    if (!user?.roles) return [];
    if (Array.isArray(user.roles)) return user.roles;
    if (typeof user.roles === 'string') {
      return user.roles.split(',').map(r => r.trim()).filter(Boolean);
    }
    return [];
  }, [user]);
  const isAdminRole = userRoles.some(role => role === 'Admin' || role === 'SuperAdmin');
  const typeMap = { hire: '入职', transfer: '调岗', resign: '离职', salary_adjust: '薪资调整', probation_confirm: '转正', promotion: '晋升', demotion: '降级', other: '其它' };
  const statusColors = { PendingApproval: 'gold', Approved: 'green', Rejected: 'red', Cancelled: 'default', PendingResign: 'orange', Resigned: 'default', Transferred: 'blue', Hired: 'green' };
  const statusLabelMap = { PendingApproval: '待审批', Approved: '已批准', Rejected: '已驳回', Cancelled: '已撤销', PendingResign: '待离职', Resigned: '已离职', Transferred: '已调岗', Hired: '已入职' };
  
  // 审批权限检查：项目经理、副经理和管理员都应该能审批
  const canApproveManager = hasPermission('employee_changes_approve_manager'); // 项目经理和副经理权限
  const canApproveSuper = isAdminRole && hasPermission('employee_changes_admin'); // 仅管理员角色且具备权限时可视为超级审批
  const isAdminApprover = canApproveSuper;
  const hasApprovalPermission = canApproveManager || canApproveSuper;
  
  // 业务函数
  const fetchList = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      if (activeTab === 'mine') {
        const params = new URLSearchParams();
        params.append('applicant_id', user.id);
        if (filters.change_type) params.append('change_type', filters.change_type);
        if (filters.status) params.append('status', filters.status);
        if (filters.keyword) params.append('keyword', filters.keyword);
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
        const res = await api.get(`/employee_changes/applications?${params.toString()}`);
        if (res.data.success) {
          setMyList(res.data.records || []);
        }
      } else if (activeTab === 'pending') {
        const params = new URLSearchParams();
        // 管理员、项目经理和副经理可以看到所有待审批的申请
        if (!hasApprovalPermission) {
          params.append('approver_id', user.id);
        }
        params.append('status', 'PendingApproval,Approved');
        if (filters.change_type) params.append('change_type', filters.change_type);
        if (filters.keyword) params.append('keyword', filters.keyword);
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
        const res = await api.get(`/employee_changes/applications?${params.toString()}`);
        if (res.data.success) {
          const recs = res.data.records || [];
          setPendingList(recs);
          // 批量获取提交人姓名（如果后端没有返回 applicant_name）
          try {
            const ids = Array.from(new Set(recs.map(r => r.applicant_id).filter(id => id)));
            const need = ids.filter(id => !employeeNameMap[id]);
            if (need.length) {
              const map = await fetchEmployeeNames(need);
              setEmployeeNameMap(prev => ({ ...prev, ...map }));
            }
          } catch (e) { /* ignore */ }
        }
      }
    } catch (e) {
      console.error('获取列表失败', e);
      message.error('获取列表失败');
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters, user?.id, hasApprovalPermission, isAdminApprover, canApproveManager]);
  const handleCreate = useCallback(async (values) => {
    try {
      // 处理日期字段格式化，确保只保留年月日
      const processedValues = { ...values };
      if (processedValues.effective_date) {
        if (dayjs.isDayjs(processedValues.effective_date)) {
          processedValues.effective_date = processedValues.effective_date.format('YYYY-MM-DD');
        } else if (typeof processedValues.effective_date === 'string') {
          processedValues.effective_date = dayjs(processedValues.effective_date).format('YYYY-MM-DD');
        }
      }
      // 只保留 candidate_name 字段
      if (processedValues.employee_id) {
        delete processedValues.employee_id;
      }
      processedValues.applicant_name = user.name || user.username || '';
      const res = await api.post('/employee_changes/applications', processedValues);
      if (!res.data.success) {
        message.error(res.data.error || '提交失败');
        return;
      }
      message.success('提交成功');
      setActiveTab('mine');
      await fetchList();
      form.resetFields();
    } catch (e) {
      if (e.response && e.response.data) {
        const err = e.response.data.error || JSON.stringify(e.response.data);
        message.error('提交失败: ' + err);
      } else {
        message.error(e.message || '提交异常');
      }
      console.error('[employee_changes] 提交异常', e);
    }
  }, [form, fetchList]);
  const handleEmployeeSearch = useCallback(async (kw) => {
    if (!kw) {
      setEmployeeOptions([]);
      return;
    }
    const keyword = kw.trim();
    if (!keyword) {
      setEmployeeOptions([]);
      return;
    }
    setEmpLoading(true);
    try {
      const [mainRes, internRes] = await Promise.allSettled([
        api.get('/employees', { params: { name: keyword } }),
        api.get('/employees', { params: { name: keyword, category: 'intern' } })
      ]);

      const mainList = mainRes.status === 'fulfilled' && Array.isArray(mainRes.value.data)
        ? mainRes.value.data.map(e => ({ ...e, record_source: e.record_source || 'main' }))
        : [];
      const internList = internRes.status === 'fulfilled' && Array.isArray(internRes.value.data)
        ? internRes.value.data.map(e => ({ ...e, record_source: 'intern' }))
        : [];

      const combined = mainList.concat(internList);
      const opts = combined.slice(0, 30).map(e => ({
        label: `${e.record_source === 'intern' ? '[实习] ' : ''}${e.name} - ${[e.department, e.section].filter(Boolean).join(' / ') || '未设置部门'}`,
        value: e.name,
        name: e.name,
        department: e.department,
        section: e.section,
        team: e.team,
        position: e.position,
        id: e.id,
        source: e.record_source || 'main'
      }));
      setEmployeeOptions(opts);
    } catch (e) {
      console.error('员工检索失败', e);
    } finally {
      setEmpLoading(false);
    }
  }, []);
  
  // 级联加载科室数据
  const loadSections = async (departmentName) => {
    try {
      // 先从缓存中查找对应的部门ID
      let dept = orgMetaCache.department.find(d => d.name === departmentName);
      
      // 如果缓存中没有找到该部门，尝试重新加载部门数据
      if (!dept) {
        const deptRes = await api.get('/org-meta?type=department&enabled_only=1');
        const departments = normalizeOrgMeta(deptRes);
        setOrgMetaCache(prev => ({
          ...prev,
          department: mergeByIdOrName(prev.department, departments)
        }));
        dept = departments.find(d => d.name === departmentName);
      }
      
      if (!dept) {
        setSectionOptions([]);
        return;
      }
      
      // 使用parent_id筛选该部门下的科室
      const res = await api.get(`/org-meta?type=section&parent_id=${dept.id}&enabled_only=1`);
      const sections = normalizeOrgMeta(res);
      setOrgMetaCache(prev => ({
        ...prev,
        section: mergeByIdOrName(prev.section, sections)
      }));
      setSectionOptions(sections.map(s => ({ label: s.name, value: s.name, id: s.id })));
      
      // 清空下级选项
      setTeamOptions([]);
      setPositionOptions([]);
      form.setFieldsValue({ target_team: undefined, target_position: undefined });
      return sections;
    } catch (e) {
      console.error('加载科室数据失败', e);
      setSectionOptions([]);
      setTeamOptions([]);
      setPositionOptions([]);
      return [];
    }
  };
  
  // 级联加载班组数据
  const loadTeams = async (sectionName) => {
    try {
      // 先从缓存中查找对应的科室ID
      let section = orgMetaCache.section.find(s => s.name === sectionName);
      
      // 如果缓存中没有找到该科室，尝试重新加载科室数据
      if (!section) {
        // 找到当前选中的部门
        const selectedDept = form.getFieldValue('target_department');
        if (selectedDept) {
          const refreshedSections = await loadSections(selectedDept);
          section = refreshedSections.find(s => s.name === sectionName) || orgMetaCache.section.find(s => s.name === sectionName);
        }
      }
      
      if (!section) {
        setTeamOptions([]);
        return;
      }
      
      // 使用parent_id筛选该科室下的班组
      const res = await api.get(`/org-meta?type=team&parent_id=${section.id}&enabled_only=1`);
      const teams = normalizeOrgMeta(res);
      setOrgMetaCache(prev => ({
        ...prev,
        team: mergeByIdOrName(prev.team, teams)
      }));
      setTeamOptions(teams.map(t => ({ label: t.name, value: t.name, id: t.id })));
      
      // 清空下级选项
      setPositionOptions([]);
      form.setFieldsValue({ target_position: undefined });
      return teams;
    } catch (e) {
      console.error('加载班组数据失败', e);
      setTeamOptions([]);
      setPositionOptions([]);
      return [];
    }
  };
  
  // 级联加载岗位数据
  const loadPositions = async (teamName) => {
    try {
      // 先从缓存中查找对应的班组ID
      let team = orgMetaCache.team.find(t => t.name === teamName);
      
      // 如果缓存中没有找到该班组，尝试重新加载班组数据
      if (!team) {
        // 找到当前选中的科室
        const selectedSection = form.getFieldValue('target_section');
        if (selectedSection) {
          const refreshedTeams = await loadTeams(selectedSection);
          team = refreshedTeams.find(t => t.name === teamName) || orgMetaCache.team.find(t => t.name === teamName);
        }
      }
      
      if (!team) {
        setPositionOptions([]);
        return;
      }
      
      // 使用parent_id筛选该班组下的岗位
      const res = await api.get(`/org-meta?type=position&parent_id=${team.id}&enabled_only=1`);
      const positions = normalizeOrgMeta(res);
      setOrgMetaCache(prev => ({
        ...prev,
        position: mergeByIdOrName(prev.position, positions)
      }));
      setPositionOptions(positions.map(p => ({ label: p.name, value: p.name, id: p.id })));
      return positions;
    } catch (e) {
      console.error('加载岗位数据失败', e);
      setPositionOptions([]);
      return [];
    }
  };
  const getCurrentUid = () => user?.id || 1;

  // 通知相关
  const fetchNotifications = async () => {};
  const markNotificationRead = async (id) => {};
  // 审批相关
  const openDecision = (record) => {
    setDecisionModal({ open: true, record });
  };
  const closeDecision = () => {
    setDecisionModal({ open: false, record: null });
  };
  const submitDecision = async (decision, opinion) => {
    console.log('[submitDecision] called', { decision, opinion, record: decisionModal.record });
    if (!decisionModal.record) return;
    const id = decisionModal.record.id;
    setDecisionLoading(true);
    try {
      const res = await api.post(`/employee_changes/applications/${id}/decision`, { decision, opinion });
      if (res.data && res.data.success) {
        message.success('决策已提交');
        closeDecision();
        fetchList();
      } else {
        message.error(res.data?.error || '提交决策失败');
      }
    } catch (e) {
      console.error('提交决策失败', e);
      message.error(e?.response?.data?.error || e.message || '提交决策异常');
    } finally {
      setDecisionLoading(false);
    }
  };
  const cancelApplication = async (record) => {
    if (!record?.id) return;
    Modal.confirm({
      title: '确认撤销该申请？',
      content: '撤销后该申请将不可恢复，是否继续？',
      okText: '确认撤销',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await api.post(`/employee_changes/applications/${record.id}/cancel`);
          if (res.data && res.data.success) {
            message.success('撤销成功');
            fetchList();
          } else {
            message.error(res.data?.error || '撤销失败');
          }
        } catch (e) {
          message.error(e?.response?.data?.error || e.message || '撤销异常');
        }
      }
    });
  };

  // 上传资料相关状态
  const [uploadModal, setUploadModal] = useState({ open: false, record: null });
  const [uploading, setUploading] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [docName, setDocName] = useState('');
  const [docTypes, setDocTypes] = useState([]);
  // 已上传资料查看
  const [docViewer, setDocViewer] = useState({ open: false, record: null, list: [], loading: false });
  

  const normalizeOrgMeta = useCallback((resp) => {
    const payload = resp?.data;
    if (Array.isArray(payload)) return payload;
    if (payload && Array.isArray(payload.records)) return payload.records;
    if (payload && Array.isArray(payload.data)) return payload.data;
    return [];
  }, []);

  const mergeByIdOrName = useCallback((existing = [], incoming = []) => {
    const map = new Map();
    existing.forEach(item => {
      if (!item) return;
      const key = item.id != null ? `id-${item.id}` : `name-${item.name ?? Math.random()}`;
      map.set(key, item);
    });
    incoming.forEach(item => {
      if (!item) return;
      const key = item.id != null ? `id-${item.id}` : `name-${item.name ?? Math.random()}`;
      map.set(key, item);
    });
    return Array.from(map.values());
  }, []);

  const formGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: '16px 20px'
  };
  const openDocViewer = async (record) => {
    setDocViewer({ open:true, list:[], record, loading:true });
    try {
      const res = await api.get(`/employee_changes/applications/${record.id}/documents`);
      if(res.data && res.data.success){
        setDocViewer({ open:true, list: res.data.records || [], record, loading:false });
      } else {
        message.error(res.data?.error || '获取资料失败');
        setDocViewer(v=>({ ...v, loading:false }));
      }
    } catch(e){
      console.error('获取资料失败', e);
      message.error('获取资料失败');
      setDocViewer(v=>({ ...v, loading:false }));
    }
  };
  const closeDocViewer = () => setDocViewer({ open:false, list:[], record:null, loading:false });

  const openUpload = (record) => {
    setUploadModal({ open: true, record });
    setUploadFiles([]);
    setDocName('');
    setDocTypes([]);
    // 设置可选的资料类型，根据变动类型动态变化
    const type = record?.change_type;
    if (type === 'resign') {
      setDocTypeOptions([
        { value: 'uniform', label: '回收工作服' },
        { value: 'resignation', label: '辞职单（会签完成）' },
        { value: 'medical', label: '离职体检报告书' }
      ]);
    } else if (type === 'transfer') {
      setDocTypeOptions([
        { value: 'training_registration', label: '培训教育登记表' }
      ]);
    } else if (type === 'hire') {
      setDocTypeOptions([
        { value: 'project_training', label: '项目部教育培训档案' },
        { value: 'level3_training', label: '三级教育培训档案' },
        { value: 'health_check', label: '入职体检报告' }
      ]);
    } else {
      setDocTypeOptions([]);
    }
  };
  const closeUpload = () => setUploadModal({ open: false, record: null });

  

  const uploadProps = {
    accept: '.pdf',
    maxCount: 1,
    beforeUpload: (file) => {
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      if (!isPdf) { message.error('只支持上传 PDF'); return Upload.LIST_IGNORE; }
      setUploadFiles([{ uid: file.uid || file.name, name: file.name, originFileObj: file }]);
      return false;
    },
    onRemove: () => setUploadFiles([]),
    fileList: uploadFiles
  };

  // 上传成功后刷新资料列表
  const handleUploadSuccess = () => {
    if (uploadModal.record?.id) fetchDocList(uploadModal.record.id);
  };

  // 上传弹窗 onOk 事件
  const submitUpload = async () => {
    if (!uploadModal.record) return;
    if (!uploadFiles.length) { message.error('请先选择 PDF 文件'); return; }
    if (!docName) { message.error('请填写资料名称'); return; }
    setUploading(true);
    try {
      // 先上传文件到通用上传接口（若存在），否则直接发送 formdata 至后端文档接口
      const formData = new FormData();
      formData.append('file', uploadFiles[0].originFileObj);
      formData.append('name', docName);
      formData.append('types', JSON.stringify(docTypes || []));
      const id = uploadModal.record.id;
  const res = await api.post(`/employee_changes/applications/${id}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data && res.data.success) {
        message.success('资料上传成功');
        // 上传成功后
        handleUploadSuccess();
        closeUpload();
        fetchList();
      } else {
        message.error(res.data?.error || '上传失败');
      }
    } catch (e) {
      console.error('上传异常', e);
      message.error(e?.response?.data?.error || e.message || '上传异常');
    } finally {
      setUploading(false);
    }
  };

  // 刷新资料列表方法
  const fetchDocList = async (applicationId) => {
    if (!applicationId) return;
    setDocViewer(v => ({ ...v, loading: true }));
    try {
      const res = await api.get(`/employee_changes/applications/${applicationId}/documents`);
      setDocViewer(v => ({ ...v, list: res.data.records || [], loading: false }));
    } catch (e) {
      setDocViewer(v => ({ ...v, list: [], loading: false }));
      message.error('资料列表加载失败');
    }
  };

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { if (notifyVisible) fetchNotifications(); }, [notifyVisible]);

  // 加载组织元数据选项（部门、科室、班组、岗位）
  useEffect(() => {
    // 初始只加载部门数据
    const fetchDepartments = async () => {
      try {
        const res = await api.get('/org-meta?type=department&enabled_only=1');
        const departments = normalizeOrgMeta(res);
        setOrgMetaCache(prev => ({
          ...prev,
          department: mergeByIdOrName(prev.department, departments)
        }));
        setDeptOptions(departments.map(d => ({ label: d.name, value: d.name, id: d.id })));
      } catch (e) {
        console.error('加载部门数据失败', e);
      }
    };
    fetchDepartments();
  }, [normalizeOrgMeta, mergeByIdOrName]);
  
  // 处理员工选择，自动填充原信息和目标信息
  const handleEmployeeSelect = useCallback(async (value, option) => {
    if (!option) return;

    form.setFieldsValue({ department: option.department || undefined });

    if (changeType !== 'transfer') {
      return;
    }

    form.setFieldsValue({
      origin_department: option.department || undefined,
      origin_section: option.section || undefined,
      origin_team: option.team || undefined,
      origin_position: option.position || undefined,
      target_department: option.department || undefined,
      target_section: undefined,
      target_team: undefined,
      target_position: undefined
    });

    if (!option.department) {
      setSectionOptions([]);
      setTeamOptions([]);
      setPositionOptions([]);
      return;
    }

    await loadSections(option.department);

    if (!option.section) {
      setTeamOptions([]);
      setPositionOptions([]);
      return;
    }

    form.setFieldsValue({ target_section: option.section });
    await loadTeams(option.section);

    if (!option.team) {
      setPositionOptions([]);
      return;
    }

    form.setFieldsValue({ target_team: option.team });
    await loadPositions(option.team);

    if (option.position) {
      form.setFieldsValue({ target_position: option.position });
    }
  }, [form, changeType, loadSections, loadTeams, loadPositions]);
  const unreadCount = notifications.filter(n => n.is_read === 0).length;

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
  { title: '类型', dataIndex: 'change_type', render: v => <Tag>{typeMap[v] || v}</Tag> },
  { title: '提交人', dataIndex: 'applicant_name', render: (v, r) => v || employeeNameMap[r.applicant_id] || (r.applicant_id && /[^0-9]/.test(String(r.applicant_id)) ? r.applicant_id : '--') },
  { title: '候选人姓名', dataIndex: 'candidate_name', render: (text) => text || '--' },
    { title: '原部门', dataIndex: 'origin_department', render: v => v || '--' },
    { title: '原科室', dataIndex: 'origin_section', render: v => v || '--' },
    { title: '原班组', dataIndex: 'origin_team', render: v => v || '--' },
    { title: '原岗位', dataIndex: 'origin_position', render: v => v || '--' },
    { title: '目标部门', dataIndex: 'target_department', render: v => v || '--' },
    { title: '目标科室', dataIndex: 'target_section', render: v => v || '--' },
    { title: '目标班组', dataIndex: 'target_team', render: v => v || '--' },
    { title: '目标岗位', dataIndex: 'target_position', render: v => v || '--' },
    { title: '生效日期', dataIndex: 'effective_date', render: v => v ? dayjs(v).format('YYYY-MM-DD') : '--' },
    { title: '当前状态', dataIndex: 'status', render: v => <Tag color={statusColors[v] || 'default'}>{statusLabelMap[v] || v}</Tag> },
    { title: '资料', render: (_, r) => {
        // 支持已批准、待离职或已完成的离职/调岗/入职申请显示资料查看入口
        const supportedTypes = ['resign', 'transfer', 'hire'];
        const canView = supportedTypes.includes(r.change_type) && (r.status === 'Approved' || r.status === 'PendingResign' || r.status === 'Resigned' || r.status === 'Transferred' || r.status === 'Hired');
        return canView ? <Button size='small' onClick={()=>openDocViewer(r)}>查看</Button> : '--';
      } },
    { title: '路由说明', dataIndex: 'route_explanation', ellipsis: true },
      { title: '操作', render: (_, r) => {
      const uid = getCurrentUid();
      const canCancel = r.status === 'PendingApproval' && r.applicant_id === uid;
      const isCurrentApprover = canApproveManager && r.routed_approver_id && r.routed_approver_id === uid;
      const canDecision = hasApprovalPermission && r.status === 'PendingApproval' && (isCurrentApprover || isAdminApprover);
  // 只有在申请已批准或已完成（离职/调岗/入职）状态下才显示上传资料按钮
  const showUploadBtn = (['resign','transfer','hire'].includes(r.change_type)) && (r.status === 'Approved' || r.status === 'Resigned' || r.status === 'Transferred' || r.status === 'Hired');
  // 我的申请列表也显示资料上传入口（同样只在已批准或已完成状态）
  const canUploadMine = (r.status === 'Approved' || r.status === 'Resigned' || r.status === 'Transferred' || r.status === 'Hired') && (['resign','transfer','hire'].includes(r.change_type)) && r.applicant_id === uid;
  // 离职执行：在 PendingResign 状态且已到生效日或有权限时显示 离职 按钮
  const isPendingResign = r.status === 'PendingResign';
  const todayYmd = dayjs().format('YYYY-MM-DD');
  const effectiveYmd = r.effective_date ? dayjs(r.effective_date).format('YYYY-MM-DD') : null;
  const canForceResign = hasPermission('manage_hr_changes') || hasPermission('employee_changes_admin') || hasPermission('employee_changes_view');
  const canExecuteResign = isPendingResign && ( (effectiveYmd && effectiveYmd <= todayYmd) || canForceResign || r.applicant_id === uid );
  
  // 调岗执行：在 Approved 状态且已到生效日或有权限时显示 调岗 按钮
  const isTransferApproved = r.change_type === 'transfer' && r.status === 'Approved';
  const canForceTransfer = hasPermission('manage_hr_changes') || hasPermission('employee_changes_admin');
  const canExecuteTransfer = isTransferApproved && ( (effectiveYmd && effectiveYmd <= todayYmd) || canForceTransfer || r.applicant_id === uid );
  
  // 入职执行：在申请类型为入职且状态为Approved时显示入职按钮
  const isHireApproved = r.change_type === 'hire' && r.status === 'Approved';
  const canForceHire = hasPermission('manage_hr_changes') || hasPermission('employee_changes_admin');
  const canExecuteHire = isHireApproved && ( (effectiveYmd && effectiveYmd <= todayYmd) || canForceHire || r.applicant_id === uid );
      return <Space>
        {/* 候选人查看，输入银行卡 */}
        
        {canDecision && <Button size='small' type='primary' onClick={() => openDecision(r)}>审批</Button>}
        {canCancel && <Button size='small' danger onClick={() => cancelApplication(r)}>撤销</Button>}
        {(showUploadBtn || canUploadMine) && <Button size='small' onClick={() => openUpload(r)}>上传资料</Button>}
        {canExecuteResign && <Button size='small' danger onClick={async ()=>{
          Modal.confirm({
            title: '确认执行离职？',
            content: '执行后该员工将从在职表迁移到离职表，操作不可逆，是否继续？',
            okText: '确认离职',
            cancelText: '取消',
            onOk: async () => {
              try {
                const res = await api.post(`/employee_changes/applications/${r.id}/execute_resign`, {});
                if (res.data && res.data.success) {
                  message.success('离职已执行');
                  fetchList();
                } else {
                  message.error(res.data?.error || '离职执行失败');
                }
              } catch (e) {
                console.error('执行离职失败', e);
                const errMsg = e?.response?.data?.error || e.message || '离职执行异常';
                if (String(errMsg).includes('尚未到生效日')) {
                  Modal.confirm({
                    title: '未到生效日，是否强制执行离职？',
                    content: '强制执行会立即完成离职迁移，请确认你有操作权限。',
                    okText: '强制执行',
                    cancelText: '取消',
                    onOk: async () => {
                      try {
                        const forceRes = await api.post(`/employee_changes/applications/${r.id}/execute_resign`, { force: true });
                        if (forceRes.data && forceRes.data.success) {
                          message.success('离职已执行');
                          fetchList();
                        } else {
                          message.error(forceRes.data?.error || '离职执行失败');
                        }
                      } catch (forceErr) {
                        console.error('强制执行离职失败', forceErr);
                        message.error(forceErr?.response?.data?.error || forceErr.message || '离职执行异常');
                      }
                    }
                  });
                  return;
                }
                message.error(errMsg);
              }
            }
          });
        }}>离职</Button>}
        {canExecuteTransfer && <Button size='small' type='primary' onClick={async ()=>{
          Modal.confirm({
            title: '确认执行调岗？',
            content: '执行后该员工的部门、岗位、科室信息将更新为目标信息，操作不可逆，是否继续？',
            okText: '确认调岗',
            cancelText: '取消',
            onOk: async () => {
              try {
                const res = await api.post(`/employee_changes/applications/${r.id}/execute_transfer`, {});
                if (res.data && res.data.success) {
                  message.success('调岗已执行');
                  fetchList();
                } else {
                  message.error(res.data?.error || '调岗执行失败');
                }
              } catch (e) {
                console.error('执行调岗失败', e);
                message.error(e?.response?.data?.error || e.message || '调岗执行异常');
              }
            }
          });
        }}>调岗</Button>}
        {canExecuteHire && <Button size='small' type='primary' onClick={async ()=>{
          Modal.confirm({
            title: '确认执行入职？',
            content: '执行后该候选人信息将被添加到实习人员表中，操作不可逆，是否继续？',
            okText: '确认入职',
            cancelText: '取消',
            onOk: async () => {
              try {
                const res = await api.post(`/employee_changes/applications/${r.id}/execute_hire`, {});
                if (res.data && res.data.success) {
                  message.success('入职已执行');
                  fetchList();
                } else {
                  message.error(res.data?.error || '入职执行失败');
                }
              } catch (e) {
                console.error('执行入职失败', e);
                message.error(e?.response?.data?.error || e.message || '入职执行异常');
              }
            }
          });
        }}>入职</Button>}
        {/* 保存银行卡后显示“培训”按钮，将候选人添加到培训委托 */}
        
      </Space>;
    } }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Card size='small' style={{ borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space size={12} wrap>
          <Button type={activeTab === 'create' ? 'primary' : 'default'} onClick={() => setActiveTab('create')}>发起申请</Button>
          <Button type={activeTab === 'mine' ? 'primary' : 'default'} onClick={() => setActiveTab('mine')}>我的申请</Button>
          {hasApprovalPermission && (
            <Button type={activeTab === 'pending' ? 'primary' : 'default'} onClick={() => setActiveTab('pending')}>
              待我审批
            </Button>
          )}
        </Space>
        <Space>
          <Button onClick={() => setNotifyVisible(true)}>通知{unreadCount > 0 && <Tag color='red' style={{ marginLeft: 4 }}>{unreadCount}</Tag>}</Button>
        </Space>
      </Card>


      {/* 筛选区 */}
      <Card size='small' style={{ borderRadius: 12 }}>
        <Form form={filterForm} layout='inline' onFinish={(vals) => {
          const next = {
            change_type: vals.filter_change_type || undefined,
            status: vals.filter_status || undefined,
            keyword: vals.filter_keyword || undefined,
            start_date: vals.filter_range?.[0] ? vals.filter_range[0].format('YYYY-MM-DD') : undefined,
            end_date: vals.filter_range?.[1] ? vals.filter_range[1].format('YYYY-MM-DD') : undefined
          };
          setFilters(next);
        }}>
          <Form.Item name='filter_change_type'>
            <Select allowClear placeholder='类型' style={{ width: 120 }} options={CHANGE_TYPES} />
          </Form.Item>
          <Form.Item name='filter_status'>
            <Select allowClear placeholder='状态' style={{ width: 130 }} options={[
              { value: 'PendingApproval', label: '待审批' },
              { value: 'Approved', label: '已批准' },
              { value: 'Rejected', label: '已驳回' },
              { value: 'Cancelled', label: '已撤销' }
            ]} />
          </Form.Item>
          <Form.Item name='filter_range'>
            <DatePicker.RangePicker />
          </Form.Item>
          <Form.Item name='filter_keyword'>
            <Input placeholder='关键字(部门/岗位/原因)' allowClear style={{ width: 200 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type='primary' htmlType='submit'>搜索</Button>
              <Button onClick={() => { filterForm.resetFields(); setFilters({}); }}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
      {activeTab === 'create' && (
        <Card title='发起人事变动申请' size='small' style={{ borderRadius: 12 }}>
          <Form layout='vertical' form={form} onFinish={handleCreate} style={{ maxWidth: 1080, width: '100%' }}>
            <Form.Item label='变动类型' name='change_type' rules={[{ required: true, message: '请选择类型' }]}> 
              <Select options={CHANGE_TYPES} placeholder='选择变动类型' allowClear onChange={v => { setChangeType(v); form.setFieldsValue({ change_type: v }); }} />
            </Form.Item>
            {/* 只有选择类型后才渲染后续表单项，避免未输入就校验 */}
            {changeType && (
              <>
                {/* 示例：入职类型 */}
                {changeType === 'hire' && (
                  <div style={formGridStyle}>
                    <Form.Item label='候选人姓名' name='candidate_name' rules={[{ required: true, message: '请填写候选人姓名' }]}>
                      <AutoComplete
                        placeholder='输入姓名检索（新员工可直接输入）'
                        options={employeeOptions}
                        onSearch={handleEmployeeSearch}
                        allowClear
                        filterOption={false}
                        notFoundContent={empLoading ? <Spin size="small" /> : null}
                      />
                    </Form.Item>
                    <Form.Item label='目标部门' name='target_department' rules={[{ required: true, message: '请选择部门' }]}> 
                      <Select
                        options={deptOptions}
                        placeholder='选择部门'
                        allowClear
                        onChange={(value) => {
                          if (value) {
                            loadSections(value);
                          } else {
                            setSectionOptions([]);
                            setTeamOptions([]);
                            setPositionOptions([]);
                            form.setFieldsValue({ target_section: undefined, target_team: undefined, target_position: undefined });
                          }
                        }}
                      />
                    </Form.Item>
                    <Form.Item label='目标科室' name='target_section'>
                      <Select
                        options={sectionOptions}
                        placeholder='选择科室'
                        allowClear
                        onChange={(value) => {
                          if (value) {
                            loadTeams(value);
                          } else {
                            setTeamOptions([]);
                            setPositionOptions([]);
                            form.setFieldsValue({ target_team: undefined, target_position: undefined });
                          }
                        }}
                      />
                    </Form.Item>
                    <Form.Item label='目标班组' name='target_team'>
                      <Select
                        options={teamOptions}
                        placeholder='选择班组'
                        allowClear
                        onChange={(value) => {
                          if (value) {
                            loadPositions(value);
                          } else {
                            setPositionOptions([]);
                            form.setFieldsValue({ target_position: undefined });
                          }
                        }}
                      />
                    </Form.Item>
                    <Form.Item label='目标岗位' name='target_position' rules={[{ required: true, message: '请选择岗位' }]}>
                      <Select options={positionOptions} placeholder='选择岗位' allowClear />
                    </Form.Item>
                    <Form.Item label='生效日期' name='effective_date' rules={[{ required: true, message: '请选择生效日期' }]}>
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label='变动原因' name='reason' rules={[{ required: true, message: '请填写变动原因' }]} style={{ gridColumn: '1 / -1' }}>
                      <Input.TextArea rows={3} placeholder='请填写变动原因' />
                    </Form.Item>
                  </div>
                )}
                {/* 调岗类型 */}
                {changeType === 'transfer' && (
                  <div style={formGridStyle}>
                    <Form.Item label='员工姓名' name='candidate_name' rules={[{ required: true, message: '请选择员工' }]}> 
                      <Select
                        showSearch
                        placeholder='输入姓名检索'
                        filterOption={false}
                        onSearch={(kw) => handleEmployeeSearch(kw)}
                        onSelect={handleEmployeeSelect}
                        options={employeeOptions}
                        notFoundContent={empLoading ? <Spin size="small" /> : null}
                      />
                    </Form.Item>
                    <Form.Item label='原部门' name='origin_department'>
                      <Select options={deptOptions} placeholder='选择原部门' allowClear />
                    </Form.Item>
                    <Form.Item label='原科室' name='origin_section'>
                      <Select options={sectionOptions} placeholder='选择原科室' allowClear />
                    </Form.Item>
                    <Form.Item label='原班组' name='origin_team'>
                      <Select options={teamOptions} placeholder='选择原班组' allowClear />
                    </Form.Item>
                    <Form.Item label='原岗位' name='origin_position'>
                      <Select options={positionOptions} placeholder='选择原岗位' allowClear />
                    </Form.Item>
                    <Form.Item label='目标部门' name='target_department' rules={[{ required: true, message: '请选择目标部门' }]}> 
                      <Select
                        options={deptOptions}
                        placeholder='选择目标部门'
                        allowClear
                        onChange={(value) => {
                          if (value) {
                            loadSections(value);
                          } else {
                            setSectionOptions([]);
                            setTeamOptions([]);
                            setPositionOptions([]);
                            form.setFieldsValue({ target_section: undefined, target_team: undefined, target_position: undefined });
                          }
                        }}
                      />
                    </Form.Item>
                    <Form.Item label='目标科室' name='target_section' rules={[{ required: true, message: '请选择目标科室' }]}> 
                      <Select
                        options={sectionOptions}
                        placeholder='选择目标科室'
                        allowClear
                        onChange={(value) => {
                          if (value) {
                            loadTeams(value);
                          } else {
                            setTeamOptions([]);
                            setPositionOptions([]);
                            form.setFieldsValue({ target_team: undefined, target_position: undefined });
                          }
                        }}
                      />
                    </Form.Item>
                    <Form.Item label='目标班组' name='target_team'> 
                      <Select
                        options={teamOptions}
                        placeholder='选择目标班组'
                        allowClear
                        onChange={(value) => {
                          if (value) {
                            loadPositions(value);
                          } else {
                            setPositionOptions([]);
                            form.setFieldsValue({ target_position: undefined });
                          }
                        }}
                      />
                    </Form.Item>
                    <Form.Item label='目标岗位' name='target_position' rules={[{ required: true, message: '请选择目标岗位' }]}>
                      <Select options={positionOptions} placeholder='选择目标岗位' allowClear />
                    </Form.Item>
                    <Form.Item label='生效日期' name='effective_date' rules={[{ required: true, message: '请选择生效日期' }]}>
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label='变动原因' name='reason' rules={[{ required: true, message: '请填写变动原因' }]} style={{ gridColumn: '1 / -1' }}>
                      <Input.TextArea rows={3} placeholder='请填写变动原因' />
                    </Form.Item>
                  </div>
                )}
                {/* 离职类型 */}
                {changeType === 'resign' && (
                  <div style={formGridStyle}>
                    <Form.Item label='员工姓名' name='candidate_name' rules={[{ required: true, message: '请选择员工' }]}> 
                      <Select
                        showSearch
                        placeholder='输入姓名检索'
                        filterOption={false}
                        onSearch={(kw) => handleEmployeeSearch(kw)}
                        onSelect={handleEmployeeSelect}
                        options={employeeOptions}
                        notFoundContent={empLoading ? <Spin size="small" /> : null}
                      />
                    </Form.Item>
                    <Form.Item label='部门' name='department' rules={[{ required: true, message: '请选择部门' }]}>
                      <Select options={deptOptions} placeholder='选择部门' allowClear />
                    </Form.Item>
                    <Form.Item label='生效日期' name='effective_date' rules={[{ required: true, message: '请选择生效日期' }]}>
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label='变动原因' name='reason' rules={[{ required: true, message: '请填写变动原因' }]} style={{ gridColumn: '1 / -1' }}>
                      <Input.TextArea rows={3} placeholder='请填写变动原因' />
                    </Form.Item>
                  </div>
                )}
                {/* 转正类型 */}
                {changeType === 'probation_confirm' && (
                  <div style={formGridStyle}>
                    <Form.Item label='员工姓名' name='candidate_name' rules={[{ required: true, message: '请选择员工' }]}> 
                      <Select
                        showSearch
                        placeholder='输入姓名检索'
                        filterOption={false}
                        onSearch={(kw) => handleEmployeeSearch(kw)}
                        onSelect={handleEmployeeSelect}
                        options={employeeOptions}
                        notFoundContent={empLoading ? <Spin size="small" /> : null}
                      />
                    </Form.Item>
                    <Form.Item label='部门' name='department' rules={[{ required: true, message: '请选择部门' }]}>
                      <Select options={deptOptions} placeholder='选择部门' allowClear />
                    </Form.Item>
                    <Form.Item label='生效日期' name='effective_date' rules={[{ required: true, message: '请选择生效日期' }]}>
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label='变动原因' name='reason' rules={[{ required: true, message: '请填写变动原因' }]} style={{ gridColumn: '1 / -1' }}>
                      <Input.TextArea rows={3} placeholder='请填写变动原因' />
                    </Form.Item>
                  </div>
                )}
                {/* 其它类型可按需补充 */}
                <Space>
                  <Button type='primary' htmlType='submit'>提交</Button>
                </Space>
              </>
            )}
          </Form>
        </Card>
      )}
      {activeTab === 'mine' && (
        <Card title='我的申请' size='small' style={{ borderRadius: 12 }}>
          <Table rowKey='id' dataSource={myList} columns={columns} loading={loading} pagination={{ pageSize: 10 }} size='small' />
        </Card>
      )}
      {activeTab === 'pending' && hasApprovalPermission && (
        <Card title='待我审批' size='small' style={{ borderRadius: 12 }}>
          <Table rowKey='id' dataSource={pendingList} columns={columns} loading={loading} pagination={{ pageSize: 10 }} size='small' />
          {pendingList.length === 0 && <Tag color='default'>暂无待审批</Tag>}
        </Card>
      )}
      <Modal open={decisionModal.open} title={`审批申请 #${decisionModal.record?.id}`} onCancel={closeDecision} footer={null} destroyOnClose>
        <p style={{ marginBottom: 8 }}>类型：{decisionModal.record?.change_type}</p>
        <p style={{ marginBottom: 8 }}>目标：{decisionModal.record?.target_department || '--'} / {decisionModal.record?.target_position || '--'}</p>
        <DecisionForm
          key={decisionModal.record?.id || 'decision-form'}
          loading={decisionLoading}
          onCancel={closeDecision}
          onSubmit={submitDecision}
        />
      </Modal>
      <Drawer title='通知中心' open={notifyVisible} width={420} onClose={() => setNotifyVisible(false)}>
        <Button size='small' onClick={fetchNotifications} loading={notifyLoading} style={{ marginBottom: 8 }}>刷新</Button>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {notifications.map(n => (
            <Card key={n.id} size='small' style={{ borderLeft: '4px solid ' + (n.is_read ? '#ccc' : '#1677ff') }}>
              <Space direction='vertical' size={4} style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600 }}>{n.event_type}</span>
                  {n.is_read === 0 && <Button size='small' onClick={() => markNotificationRead(n.id)}>标记已读</Button>}
                </div>
                <div style={{ fontSize: 12, color: '#555' }}>{n.created_at}</div>
                <div style={{ fontSize: 12 }}>{(() => { try { const p = JSON.parse(n.payload || '{}'); return Object.entries(p).map(([k, v]) => `${k}: ${v}`).join(' | '); } catch (e) { return n.payload; } })()}</div>
              </Space>
            </Card>
          ))}
          {!notifications.length && <Tag color='default'>暂无通知</Tag>}
        </div>
      </Drawer>
      <Modal open={uploadModal.open} title={`上传资料 #${uploadModal.record?.id}`} onCancel={closeUpload} onOk={submitUpload} okText='上传' confirmLoading={uploading}>
        <Form layout='vertical'>
          <Form.Item label='资料名称' required>
            <Input value={docName} onChange={e => setDocName(e.target.value)} placeholder='请输入资料名称' />
          </Form.Item>
          <Form.Item label='资料类型'>
            <Select mode='multiple' placeholder='选择资料类型' value={docTypes} onChange={v => setDocTypes(v)} options={docTypeOptions} />
          </Form.Item>
          <Form.Item label='PDF 文件'>
            <Upload {...uploadProps} showUploadList fileList={uploadFiles} onRemove={() => setUploadFiles([])}>
              <Button>选择 PDF</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
      
      <Modal open={docViewer.open} title={`资料列表 #${docViewer.record?.id}`} onCancel={closeDocViewer} footer={null} width={560}>
        {docViewer.loading && <Spin />}
        {!docViewer.loading && (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {docViewer.list.map(d => {
              let types=[]; try{ types = JSON.parse(d.doc_types||'[]'); if(!Array.isArray(types)) types=[]; }catch(e){ types=[]; }
              const downloadUrl = d.download_url || '';
              return (
                <Card key={d.id} size='small'>
                  <Space direction='vertical' size={4} style={{ width:'100%' }}>
                    <Space style={{ justifyContent:'space-between', width:'100%' }}>
                      <strong>{d.doc_name || d.file_name}</strong>
                      <span style={{ fontSize:12 }}>{(d.file_size/1024).toFixed(1)} KB</span>
                    </Space>
                    <div style={{ fontSize:12, color:'#555' }}>上传时间: {d.created_at}</div>
                    <Space wrap>
                      {types.map(t => <Tag key={t}>{t}</Tag>)}
                    </Space>
                    <Space>
                      <Button size='small' type='primary' onClick={() => setPdfPreview({ open: true, url: downloadUrl, fileName: d.doc_name || d.file_name })} disabled={!downloadUrl}>预览</Button>
                      {((d.uploader_id && d.uploader_id === user?.id) || (docViewer.record && docViewer.record.applicant_id === user?.id) || hasPermission('employee_changes_admin')) && (
                        <Button size='small' danger onClick={async () => {
                          try {
                            await api.delete(`/employee_changes/applications/${docViewer.record?.id}/documents/${d.id}`);
                            message.success('删除成功');
                            if (docViewer.record?.id) fetchDocList(docViewer.record.id);
                          } catch (err) {
                            const status = err?.response?.status;
                            if (status === 403) message.error('无权删除该资料');
                            else message.error('删除失败');
                          }
                        }}>删除</Button>
                      )}
                      <Button size='small' type='link' onClick={() => openWithAuth(downloadUrl)} disabled={!downloadUrl}>下载</Button>
                    </Space>
                  </Space>
                </Card>
              );
            })}
            {!docViewer.list.length && <Tag color='default'>暂无资料</Tag>}
          </div>
        )}
      </Modal>
      {/* PDF 预览弹窗 */}
      <Modal open={pdfPreview.open} title={pdfPreview.fileName} onCancel={() => setPdfPreview({ open: false, url: '', fileName: '' })} footer={null} width={800}>
        {pdfPreview.url && <SimplePdfViewer url={pdfPreview.url} fileName={pdfPreview.fileName} />}
      </Modal>
    </div>
  );
}


// 审批决策表单，确保切换审批对象时表单重置
function DecisionForm({ loading, onCancel, onSubmit }) {
  const [form] = Form.useForm();
  return (
    <Form
      form={form}
      layout='vertical'
      onFinish={(vals) => { onSubmit(vals.decision, vals.opinion); }}
    >
      <Form.Item label='审批意见' name='opinion' rules={[{ required: true, message: '请填写审批意见' }]}> 
        <Input.TextArea rows={4} placeholder='请输入审批意见' onChange={(e)=>form.setFieldsValue({ opinion: e.target.value })} /> 
      </Form.Item>
      <Form.Item label='决策' name='decision' rules={[{ required: true, message: '请选择决策' }]}> 
        <Select allowClear placeholder='请选择决策' onChange={(v)=>form.setFieldsValue({ decision: v })}>
          <Select.Option value='approve'>同意</Select.Option>
          <Select.Option value='reject'>驳回</Select.Option>
        </Select>
      </Form.Item>
      <Form.Item>
        <Space>
          <Button onClick={onCancel}>取消</Button>
          <Button type='primary' htmlType='submit' loading={loading}>提交决策</Button>
        </Space>
      </Form.Item>
    </Form>
  );
}

export default EmployeeChangesPage;